import React, { useState, useEffect } from 'react';
import api from '../services/apiService';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { decode } from 'he';
import LoSyncStatus from './LoSyncStatus';
import { usePerms } from '../hooks/useAuth';

const empty = { isActive:false, loPriceMode:'request', loSaleAmount:'', titleOverride:'', descriptionOverride:'', notes:'', imageUrl:'', location:'', conditionId:'' };
const publicText = value => decode(String(value ?? '').replace(/<br\s*\/?\s*>|<\/p>/gi, '\n').replace(/<[^>]*>/g, '')).trim();
export default function ItemWebChannelLoEdit() {
  const { has } = usePerms();
  const { itemId } = useParams();
  const [loading,setLoading] = useState(true), [saving,setSaving] = useState(false), [error,setError] = useState(''), [message,setMessage] = useState('');
  const [article,setArticle] = useState({}), [conditions,setConditions] = useState([]), [values,setValues] = useState(empty), [refresh,setRefresh] = useState(0);
  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        const [item,cond,channels] = await Promise.all([api.get(`/items/${itemId}`),api.get('/conditions'),api.get(`/item-web-channels/item/${itemId}`)]);
        if (!active) return;
        setArticle(item.data); setConditions(cond.data || []);
        const lo = channels.data.find(ch => ch.channelKey === 'LO');
        if (lo) setValues(Object.fromEntries(Object.entries(empty).map(([key,fallback]) => [key,lo[key] ?? fallback])));
      } catch (e) { if (active) setError(e.response?.data?.error || 'LO-Web-Channel konnte nicht geladen werden.'); }
      finally { if (active) setLoading(false); }
    };
    load(); return () => { active = false; };
  }, [itemId]);
  const change = (key,value) => setValues(current => ({ ...current,[key]:value }));
  const save = async e => {
    e.preventDefault(); setSaving(true); setError(''); setMessage('');
    try {
      await api.post(`/item-web-channels/item/${itemId}`, { ...values, channelKey:'LO', conditionId:values.conditionId || null, loSaleAmount:values.loPriceMode === 'fixed' ? values.loSaleAmount : null });
      setMessage(values.isActive ? 'Gespeichert. Die Übertragung wird im Backend verarbeitet.' : 'Gespeichert. Die Rücknahme wird im Backend verarbeitet.');
      setRefresh(value => value + 1);
    } catch (err) { setError(err.response?.data?.error || 'LO-Web-Channel konnte nicht gespeichert werden.'); }
    finally { setSaving(false); }
  };
  if (loading) return <div>Lade LO-Web-Channel …</div>;
  const articleTitle = [article.manufacturer, article.manufacturerType].map(publicText).filter(Boolean).join(' - ') || publicText(article.name);
  const articleDescription = publicText(article.description);
  const usedDescription = publicText(values.descriptionOverride) || articleDescription;
  return <div className="form">
    <Helmet><title>LO Web-Channel bearbeiten</title></Helmet>
    <h2>LO Web-Channel bearbeiten</h2><p>Artikel {itemId} · {article.name}</p>
    <p>
      <Link to="/itemWebChannelListLO">Zur LO-Liste</Link>
      {has('/lo-integration') && (
        <>{' · '}<Link to="/lo-integration">Verbindung und Feldfreigaben</Link></>
      )}
    </p>
    {error && <p role="alert">{error}</p>}{message && <p role="status">{message}</p>}
    <form onSubmit={save}>
      <div className="form-row"><div className="col">
        <label>Zustand<select value={values.conditionId} onChange={e => change('conditionId',e.target.value)} style={{ margin: '0 0 10px' }}><option value="">Zustand wählen</option>{conditions.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}</select></label>
        <label>Standort<input value={values.location} onChange={e => change('location',e.target.value)} maxLength={120} placeholder="Ort, sonst LO-Standort aus Artikel"/></label>
        <label>Titel<input value={values.titleOverride} onChange={e => change('titleOverride',e.target.value)} maxLength={160} placeholder={articleTitle || 'Titel der Maschine'} aria-describedby="lo-title-help"/></label>
        <p id="lo-title-help">{articleTitle ? `Leer lassen übernimmt „${articleTitle}“ aus dem Artikel.` : 'Bitte einen Titel ergänzen; im Artikel fehlt ein verwendbarer Titel.'}</p>
        <label>Beschreibung<textarea value={values.descriptionOverride} onChange={e => change('descriptionOverride',e.target.value)} rows={8} maxLength={12000} placeholder={articleDescription || 'Kurze Beschreibung der Maschine'} aria-describedby="lo-description-help"/></label>
        <p id="lo-description-help"> Verwendeter Text: {usedDescription.length} von 12.000 Zeichen.</p>
      </div><div className="col">
        <label>Verkaufspreis<select value={values.loPriceMode} onChange={e => change('loPriceMode',e.target.value)} style={{ margin: '0 0 10px' }}><option value="request">Preis auf Anfrage</option><option value="fixed">Festpreis netto (EUR)</option></select></label>
        {values.loPriceMode === 'fixed' && <label>Netto-Verkaufspreis<input type="text" inputMode="decimal" value={values.loSaleAmount} onChange={e => change('loSaleAmount',e.target.value)} required={values.isActive} placeholder="24500,00"/></label>}
        
        <label>Bildadresse (HTTPS)<input type="text" value={values.imageUrl} onChange={e => change('imageUrl',e.target.value)} placeholder="Sonst LO-Bild / HP-Bild aus Artikel"/></label>
        <label>Öffentliche Ausstattung / LO-Notizen (optional)<textarea value={values.notes} onChange={e => change('notes',e.target.value)} maxLength={300} rows={4} aria-describedby="lo-notes-help"/></label>
        
      </div><div className="col">
        <div style={{ display: 'flex', gap: '20px', alignItems: 'center', justifyContent: 'center', marginBottom: '10px' }}>
          <label style={{ textAlign: 'center' }}>
            <input
              type="checkbox"
              checked={values.isActive}
              onChange={e => change('isActive',e.target.checked)}
            />
            Auf Liftoutlet veröffentlichen
          </label>
        </div>
      </div></div>
      <button type="submit" className="submit-button" disabled={saving}>{saving ? 'Speichert …' : 'LO-Web-Channel speichern'}</button>
    </form>
    <LoSyncStatus itemId={itemId} refresh={refresh}/>
  </div>;
}
